import { Tooltip } from 'antd';
import { differenceInMinutes, format, startOfDay } from 'date-fns';
import {
  customMillisecondsToHours,
  shiftTimeFormat,
} from '../../lib/utils/date-time';
import type {
  Shift,
  ShiftBackgroundColours,
  ShiftCellRecord,
  ShiftPeriod,
} from './types';
import ShiftBackground from './ShiftBackground';
import { userFullName } from '../../lib/utils';
import ShiftWarningTeamButton from '../Teams/TeamRoster/ShiftWarningTeamButton';
import {
  minutesPerDay,
  minutesPerHour,
  shiftDivOffsetPx,
} from './lib/constants';

type ShiftDivProps = {
  readonly index: number;
  readonly shift: Shift;
  readonly cellRecord: ShiftCellRecord;
  readonly dayShift: ShiftPeriod;
  readonly shiftBackgroundColours: ShiftBackgroundColours;
  readonly cellWidth?: number;
  readonly team?: {
    readonly id?: string;
    readonly name?: string;
  };
  readonly violations: {
    readonly consecutiveShiftViolation: boolean;
    readonly maxShiftHoursViolation: boolean;
    readonly shiftRestGapViolation: boolean;
  };
};

type ShiftDivStylesParams = {
  readonly cellDivWidthInPixel: number;
  readonly shiftStartMinutesOfDay: number;
  readonly shiftDurationInMinutes: number;
};

/**
 * Shift button's styles for width and left position (in pixels), so the Shift looks like temporal.
 */
const shiftDivDynamicStyles = ({
  cellDivWidthInPixel,
  shiftStartMinutesOfDay,
  shiftDurationInMinutes,
}: ShiftDivStylesParams) => {
  const shiftDivWidthInPixelPerMinute = cellDivWidthInPixel / minutesPerDay;

  const shiftDivWidthInPixelPerHour =
    minutesPerHour * shiftDivWidthInPixelPerMinute;

  // Minimum width of the button, if shift button is less than this width, we don't display hour text, so it is not truncated.
  const shiftDivMinWidthInPixel = 5 * shiftDivWidthInPixelPerHour;

  const shiftDivLeftPositionInPixel =
    shiftStartMinutesOfDay * shiftDivWidthInPixelPerMinute;

  const shiftDivWidthInPixel =
    shiftDivWidthInPixelPerMinute * shiftDurationInMinutes;

  return {
    shiftDivWidth: Math.max(shiftDivMinWidthInPixel, shiftDivWidthInPixel),
    shiftDivLeftPosition: shiftDivLeftPositionInPixel + shiftDivOffsetPx,
    hideHourText: true,
  };
};

/**
 * A div with calculated dynamic width and position represents a Shift in a roster table.
 * to be used when the button is not needed.
 */
const ViolationDiv = ({
  index,
  shift,
  cellRecord,
  dayShift,
  shiftBackgroundColours,
  cellWidth,
  team,
  violations,
}: ShiftDivProps) => {
  const cellDivWidthInPixel = cellWidth ?? 0;

  const startTime = new Date(shift.startTime);

  const endTime = new Date(shift.endTime);

  const shiftDurationInMinutes = differenceInMinutes(endTime, startTime);

  const shiftStartMinutesOfDay = differenceInMinutes(
    startTime,
    startOfDay(startTime)
  );

  const { shiftDivWidth, shiftDivLeftPosition } = shiftDivDynamicStyles({
    shiftDurationInMinutes,
    shiftStartMinutesOfDay,
    cellDivWidthInPixel,
  });

  const shiftDurationInHours = customMillisecondsToHours(
    endTime.getTime() - startTime.getTime()
  );

  const userName = userFullName({
    first_name: cellRecord.firstName,
    last_name: cellRecord.lastName,
  });

  const isVacant = cellRecord.userRoleAssignmentId === undefined;

  const accessibleStyle = { display: 'block', color: '#ffffff' };

  const tooltip = (
    <>
      {[
        cellRecord.roleName,
        violations.consecutiveShiftViolation
          ? 'Consecutive Shift Violation'
          : undefined,
        violations.shiftRestGapViolation
          ? 'Shift Rest Gap Violation'
          : undefined,
        violations.maxShiftHoursViolation
          ? 'max Shift Hours Violation'
          : undefined,
      ]
        .filter((item) => item !== undefined)
        .map((item, index) => (
          <span style={accessibleStyle} key={`${index}-${shift.id}`}>
            {item}
          </span>
        ))}
      <ShiftWarningTeamButton team={team} light={true} />
      {
        <div className="shift-tooltip-username" style={{ color: '#ffffff' }}>
          {userName}
        </div>
      }
      <span style={accessibleStyle}>{`${format(
        startTime,
        shiftTimeFormat
      )} to ${format(
        endTime,
        shiftTimeFormat
      )} (${shiftDurationInHours}h)`}</span>
    </>
  );

  const shiftDivStyles = {
    padding: 0,
    zIndex: 2,
    border: 'none',
    cursor: 'pointer',
    minHeight: '65px',
    left: shiftDivLeftPosition,
    width: shiftDivWidth,
    display: 'block',
  };

  const hasViolation = Object.values(violations).some((violation) => violation);

  return (
    <>
      <Tooltip
        title={tooltip}
        color="#000000"
        overlayInnerStyle={{ fontWeight: 500 }}
      >
        <div
          style={{ ...shiftDivStyles, position: 'relative' }}
          id={`shift-information-div-${index}-${shift.id}`}
          className={'shift-information-div'}
          title={startTime.toDateString()}
        >
          <div style={{ display: 'block', overflow: 'auto' }}>
            <ShiftBackground
              dayShiftPeriod={dayShift}
              shift={shift}
              shiftBackgroundColours={shiftBackgroundColours}
              isVacant={isVacant}
              hasViolation={hasViolation}
            />
            <div
              style={{
                display: 'block',
                position: 'relative',
                width: '100%',
                overflow: 'auto',
                padding: '2px',
                color: '#ffffff',
              }}
            >
              <></>
            </div>
          </div>
        </div>
      </Tooltip>
    </>
  );
};

export default ViolationDiv;
